import deliveryService from "../../services/deliveryNoteService";
import customerReturnService from "../../services/customerReturnService";
import { remainingTicketLines } from "../../helpers/customerReturnTicket";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  Label,
  FormGroup,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { productActions } from "../../sagas/productSlice";
import { clientActions } from "../../sagas/clientSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import productService from "../../services/productService";

import { formatPrice } from "../../helpers/utility";
import {
  FaCartPlus,
  FaImage,
  FaList,
  FaSearch,
  FaShoppingCart,
  FaTimes,
  FaThLarge,
  FaTrash,
  FaTrashAlt,
  FaUser,
  FaEraser,
  FaReceipt,
  FaKeyboard,
  FaMoneyBillWave,
  FaCreditCard,
} from "react-icons/fa";

import "./CustomerReturnFormPOS.css";
import config from "../../helpers/config";
import POSKeypadModal from "./POSKeypadModal";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import POSHeader from "./POSHeader";
import { CBL_PAGE, CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, PAY_PAGE, POS_PAGE, RETOUR_FORM_NEW, RETOUR_PAGE } from "../../routes/routeConstants";
import { useLocation, useNavigate } from "react-router-dom";

const CustomerReturnForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const location = useLocation();
  const ticketRequestRef = useRef(0);
  const customerReturn = location.state?.customerReturn;

  const isEditing = Boolean(customerReturn);
  const canBeUpdated =
    (isEditing && customerReturn?.statut === "BROUILLON") || !isEditing;
  const { currentShift, loading: shiftLoading, error: shiftError } = useSelector(
    (state) => state.shift
  );
  const { newReturnNumber, loading } = useSelector(
    (state) => state.customerReturns
  );
  const { clients } = useSelector((state) => state.clients);
  const { list: deliverySearchResults = [], loading: deliveryLoading } = useSelector((state) => state.deliveryNotes || {});
  const {
    products,
    totalPages,
    totalElements,
    pageSize,
    loading: productsLoading,
  } = useSelector((s) => s.products);
  const { businessName, email, role, address, telephone, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("RETOURS");
  const unites = useSelector((s) => s.products.unites);
  const navigate = useNavigate();

  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  // ===== Modal Client =====
  const [clientModal, setClientModal] = useState(false);
  const toggleClientModal = () => setClientModal((v) => !v);
  const [clientSearch, setClientSearch] = useState("");
  const [textKeypadOpen, setTextKeypadOpen] = useState(false);
  const [textKeypadValue, setTextKeypadValue] = useState("");
  const [textFieldName, setTextFieldName] = useState(null);

  const [numberKeypadOpen, setNumberKeypadOpen] = useState(false);
  const [numberKeypadValue, setNumberKeypadValue] = useState("");
  const [numberField, setNumberField] = useState(null);
  const [returnSourceMode, setReturnSourceMode] = useState("MANUAL");
  const [displayMode, setDisplayMode] = useState("list");
  const [paymentModal, setPaymentModal] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  const [loadedTicketNumber, setLoadedTicketNumber] = useState("");
  const [ticketKeypadOpen, setTicketKeypadOpen] = useState(false);
  const [ticketSearchPerformed, setTicketSearchPerformed] = useState(false);
  const [defaultPassagerClientId, setDefaultPassagerClientId] = useState("");
  const searchInputRef = useRef(null);
  const formValuesRef = useRef(null);
  const setFieldValueRef = useRef(null);
  const submitStatusRef = useRef(null);

  const isAdmin = role === "ROLE_ADMIN";
  const getLocalISODate = () => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().split("T")[0];
  };

  // ===== INIT =====
  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));
    dispatch(productActions.findAllUnites());
    if (!isEditing) dispatch(customerReturnActions.generateNumero());
  }, [dispatch, isEditing]);

  useEffect(() => {
    if (isEditing || defaultPassagerClientId) return;
    const passager = (clients || []).find((c) => {
      const label =
        c.typeClient === "PARTICULIER"
          ? `${c.nom || ""} ${c.prenom || ""}`.trim()
          : c.entreprise || `${c.nom || ""} ${c.prenom || ""}`.trim();
      return label.toUpperCase() === "PASSAGER";
    });

    if (passager?.id) {
      setDefaultPassagerClientId(passager.id);
    }
  }, [clients, defaultPassagerClientId, isEditing]);

  useEffect(() => {
    const existingNumBL = customerReturn?.numBL || "";
    if (!existingNumBL) return;

    setTicketNumber(existingNumBL);
    setLoadedTicketNumber(existingNumBL);
    setReturnSourceMode("TICKET");
  }, [customerReturn?.numBL]);

  // Load products when modal opens or pagination changes
  useEffect(() => {
    dispatch(
      productActions.findAll({
        search: modalSearchTerm || "",
        page: currentPage - 1,
        size: pageSize || 10,
      })
    );
  }, [dispatch, currentPage, modalSearchTerm, pageSize]);

  const triggerProductSearch = (query) => {
    setSearchValue(query);
    setModalSearchTerm(query);
    setCurrentPage(1);
    dispatch(productActions.findAll({
      search: query,
      page: 0,
      size: pageSize || 10,
    }));
  };

  const resetSearchAfterScan = useCallback(() => {
    setSearchValue("");
    setModalSearchTerm("");
    setCurrentPage(1);
    dispatch(
      productActions.findAll({
        search: "",
        page: 0,
        size: pageSize || 10,
      })
    );
  }, [dispatch, pageSize]);

  useEffect(() => {
    const focusSearchInput = () => {
      if (returnSourceMode !== "MANUAL") return;

      const activeEl = document.activeElement;
      const tag = activeEl?.tagName?.toLowerCase();
      const isTypingSomewhere =
        activeEl &&
        activeEl !== searchInputRef.current &&
        (tag === "input" || tag === "textarea" || activeEl?.isContentEditable);

      if (isTypingSomewhere) return;
      searchInputRef.current?.focus();
    };

    focusSearchInput();
    window.addEventListener("click", focusSearchInput);
    return () => window.removeEventListener("click", focusSearchInput);
  }, [returnSourceMode]);

  useEffect(() => {
    if (returnSourceMode !== "MANUAL") return;
    if (searchKeypadOpen || clientModal || numberKeypadOpen || textKeypadOpen) return;
    const id = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [searchKeypadOpen, clientModal, numberKeypadOpen, textKeypadOpen, returnSourceMode]);

  // ===== HELPERS =====
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const recalcLineAmounts = (line) => {
    const qte = Number(line.quantiteRetournee) || 0;
    const prixTTC = Number(line.prixUnitaireTTC) || 0;
    const taux = Number(line.tauxTVA) || 0;

    const montantTTC = round2(qte * prixTTC);
    const montantHT = taux > 0 ? round2(montantTTC / (1 + taux)) : montantTTC;
    const montantTVA = round2(montantTTC - montantHT);

    return { ...line, montantHT, montantTVA, montantTTC };
  };

  const calculateTotals = (lines) => {
    let totalHT = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    (lines || []).forEach((line) => {
      const qte = Number(line.quantiteRetournee) || 0;
      const ttc = Number(line.prixUnitaireTTC) || 0;
      const taux = Number(line.tauxTVA) || 0;

      const lineTotalTTC = round2(qte * ttc);
      const lineHT = taux > 0 ? round2(lineTotalTTC / (1 + taux)) : lineTotalTTC;
      const lineTVA = round2(lineTotalTTC - lineHT);

      totalTTC += lineTotalTTC;
      totalHT += lineHT;
      totalTVA += lineTVA;
    });

    return {
      montantHT: round2(totalHT),
      montantTVA: round2(totalTVA),
      montantTTC: round2(totalTTC),
    };
  };

  const addProductToReturnLines = useCallback(
    (p) => {
      const values = formValuesRef.current;
      const setFieldValue = setFieldValueRef.current;
      if (!values || !setFieldValue || !p?.id || !canBeUpdated || returnSourceMode !== "MANUAL") return false;

      const existingIndex = (values.lignes || []).findIndex(
        (l) => String(l.articleId) === String(p.id) || String(l.article) === String(p.id)
      );
      if (existingIndex >= 0) {
        const currentLine = values.lignes[existingIndex];
        const updatedLine = recalcLineAmounts({
          ...currentLine,
          quantiteRetournee: (Number(currentLine.quantiteRetournee) || 0) + 1,
        });
        const updatedLines = [...values.lignes];
        updatedLines[existingIndex] = updatedLine;
        setFieldValue("lignes", updatedLines);
        return false;
      }

      const newLine = recalcLineAmounts({
        article: p.id,
        articleId: p.id,
        articleLibelle: p.libelle,
        articleCode: p.code,
        quantiteRetournee: 1,
        quantiteOriginale: 1,
        prixUnitaireHT:
          p.prixVenteHT ||
          Number(p.prixVenteTTC1 || 0) / (1 + Number(p.tauxTVA || 0.2)),
        prixUnitaireTTC: p.prixVenteTTC1,
        tauxTVA: p.tauxTVA || 0.2,
        unite: p.unite,
      });

      setFieldValue("lignes", [...values.lignes, newLine]);
      return true;
    },
    [canBeUpdated, recalcLineAmounts, returnSourceMode, t]
  );

  const handleBarcodeScan = useCallback(
    async (rawCode) => {
      if (returnSourceMode !== "MANUAL") return;

      const barcode = (rawCode || "").trim();
      if (!barcode) return;

      setSearchValue(barcode);

      try {
        const data = await productService.findAll(barcode, 0, 50);
        const items = data?.body?.content || data?.content || [];
        const normalized = barcode.toLowerCase();
        const product =
          items.find((p) =>
            [p.codeBarre, p.barcode, p.code]
              .filter(Boolean)
              .some((v) => String(v).trim().toLowerCase() === normalized)
          ) || items[0];

        if (product?.id) {
          addProductToReturnLines(product);
        } else {
          triggerProductSearch(barcode);
        }
      } catch (e) {
        triggerProductSearch(barcode);
      } finally {
        resetSearchAfterScan();
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    },
    [addProductToReturnLines, resetSearchAfterScan, returnSourceMode, triggerProductSearch]
  );

  useEffect(() => {
    let buffer = "";
    let timeout;

    const handleScan = (e) => {
      if (searchKeypadOpen || clientModal || numberKeypadOpen || textKeypadOpen) return;

      if (timeout) clearTimeout(timeout);

      if (e.key === "Enter") {
        if (buffer.length >= 4) {
          e.preventDefault();
          handleBarcodeScan(buffer);
        }
        buffer = "";
        return;
      }

      const activeEl = document.activeElement;
      const tag = activeEl?.tagName?.toLowerCase();
      const isTypingElsewhere =
        activeEl &&
        activeEl !== searchInputRef.current &&
        (tag === "input" || tag === "textarea" || activeEl?.isContentEditable);
      if (isTypingElsewhere) return;

      if (e.key.length === 1) {
        buffer += e.key;
        timeout = setTimeout(() => (buffer = ""), 100);
      }
    };

    window.addEventListener("keydown", handleScan);
    return () => {
      window.removeEventListener("keydown", handleScan);
      if (timeout) clearTimeout(timeout);
    };
  }, [searchKeypadOpen, clientModal, numberKeypadOpen, textKeypadOpen, handleBarcodeScan]);

  // ===== CLIENT LIST (modal) =====
  const clientList = useMemo(() => {
    const q = (clientSearch || "").trim().toLowerCase();

    const mapped =
      (clients || []).map((c) => {
        const label =
          c.typeClient === "PARTICULIER"
            ? `${c.nom || ""} ${c.prenom || ""}`.trim()
            : c.entreprise || `${c.nom || ""} ${c.prenom || ""}`.trim();

        return {
          id: c.id,
          code: c.code || "",
          label,
          raw: c,
        };
      }) || [];

    if (!q) return mapped;

    return mapped.filter((c) => {
      const str = `${c.code} ${c.label}`.toLowerCase();
      return str.includes(q);
    });
  }, [clients, clientSearch]);

  const getClientLabelById = (id) => {
    if (!id) return "-";
    const c = (clients || []).find((x) => String(x.id) === String(id));
    if (!c) return "-";
    return c.typeClient === "PARTICULIER"
      ? `${c.nom || ""} ${c.prenom || ""}`.trim()
      : c.entreprise || `${c.nom || ""} ${c.prenom || ""}`.trim();
  };

  // ===== VALIDATION =====
  const validationSchema = Yup.object().shape({
    dateOperation: Yup.date().required(
      t("customerReturns.form.validation.dateRequired")
    ),

    lignes: Yup.array().min(
      1,
      t("customerReturns.form.validation.itemsRequired")
    ),
  });

  const initialValues =
    isEditing && customerReturn
      ? {
        numeroRetour: customerReturn.numeroRetour || "",
        dateOperation:
          customerReturn.dateOperation ||
          getLocalISODate(),
        clientId: customerReturn.clientId || "",
        numBL: customerReturn.numBL || "",
        motifRetour: customerReturn.motifRetour || "",
        modeRemboursement: customerReturn.modeRemboursement || "AVOIR",
        montantPayeCash: Number(customerReturn.montantPayeCash || 0),
        montantPayeCarte: Number(customerReturn.montantPayeCarte || 0),
        montantPaye:
          Number(customerReturn.montantPayeCash || 0) +
          Number(customerReturn.montantPayeCarte || 0),
        lignes:
          customerReturn.lignes?.map((line) => ({
            articleId: line.articleId,
            articleLibelle: line.articleNom,
            articleCode: line.articleCode,
            quantiteRetournee: line.quantiteRetournee,
            quantiteOriginale: line.quantiteOriginale,
            prixUnitaireHT: line.prixUnitaireHT,
            prixUnitaireTTC: line.prixUnitaireTTC,
            tauxTVA: line.tauxTVA,
            unite: line.unite,
          })) || [],
      }
      : {
        numeroRetour: newReturnNumber,
        dateOperation: getLocalISODate(),
        clientId: defaultPassagerClientId || "",
        numBL: "",
        motifRetour: "",
        modeRemboursement: "AVOIR",
        montantPayeCash: 0,
        montantPayeCarte: 0,
        montantPaye: 0,
        lignes: [],
      };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    const totals = calculateTotals(values.lignes);
    const forcedStatus = submitStatusRef.current;
    submitStatusRef.current = null;

    const dataToSubmit = {
      ...values,
      ...totals,
      modeRemboursement:
        forcedStatus === "VALIDE" &&
        (Number(values.montantPayeCash || 0) > 0 || Number(values.montantPayeCarte || 0) > 0)
          ? "REMBOURSEMENT"
          : values.modeRemboursement,
      montantPayeCash: Number(values.montantPayeCash || 0),
      montantPayeCarte: Number(values.montantPayeCarte || 0),
      numBL: values.numBL || loadedTicketNumber || "",
      shiftId: currentShift?.id,
      isPassager: values.clientId ? false : true,
      ...(forcedStatus ? { statut: forcedStatus } : { statut: "BROUILLON" }),
    };
    const resetAndStay = () => {
      setSubmitting(false);
      resetForm();
      setReturnSourceMode("MANUAL");
      setTicketNumber("");
      setLoadedTicketNumber("");
      setTicketSearchPerformed(false);
      setSearchValue("");
      setModalSearchTerm("");
      setCurrentPage(1);
      setPaymentModal(false);
      if (!isEditing) {
        dispatch(customerReturnActions.generateNumero());
      }
    };

    if (isEditing) {
      dispatch(
        customerReturnActions.update({
          id: customerReturn?.id,
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            resetAndStay();
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: error?.response?.data?.message || error?.message || t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
            setPaymentModal(false);
          },
        })
      );
    } else {
      dispatch(
        customerReturnActions.create({
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            resetAndStay();
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: error?.response?.data?.message || error?.message || t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
            setPaymentModal(false);
          },
        })
      );
    }
  };

  if (loading && isEditing) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="pos-form-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(POS_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
        }}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting, setFieldValue, submitForm }) => {
          formValuesRef.current = values;
          setFieldValueRef.current = setFieldValue;
          const totals = calculateTotals(values.lignes);
          const lineCount = values.lignes?.length || 0;
          const canSave = canBeUpdated && lineCount > 0;
          const canAddProducts = canBeUpdated && returnSourceMode === "MANUAL";
          const productList = products || [];
          const paidCash = Number(values.montantPayeCash || 0);
          const paidCard = Number(values.montantPayeCarte || 0);
          const paidTotal = paidCash + paidCard;

          const selectReturnPayment = (mode) => {
            const total = Number(totals.montantTTC || 0);
            setFieldValue("modeRemboursement", "REMBOURSEMENT");
            if (mode === "CARTE_BANCAIRE") {
              setFieldValue("montantPayeCash", 0);
              setFieldValue("montantPayeCarte", total);
              setFieldValue("montantPaye", total);
              return;
            }
            setFieldValue("montantPayeCash", total);
            setFieldValue("montantPayeCarte", 0);
            setFieldValue("montantPaye", total);
          };

          const openReturnCheckout = () => {
            if (!canSave) return;
            const hasPayment = paidCash > 0 || paidCard > 0;
            if (!hasPayment) {
              selectReturnPayment("ESPECES");
            }
            setPaymentModal(true);
          };

          const confirmReturnCheckout = () => {
            if (paidTotal <= 0) {
              Swal.fire({
                icon: "warning",
                title: t("common.information"),
                text: "Veuillez sélectionner un mode de paiement.",
              });
              return;
            }
            submitStatusRef.current = "VALIDE";
            setPaymentModal(false);
            submitForm();
          };

          const addProductToCurrentReturn = (p) => {
            if (!canAddProducts) return;

            const already = (values.lignes || []).some(
              (l) => String(l.articleId) === String(p.id) || String(l.article) === String(p.id)
            );
            if (already) {
              toast.warning(t("customerReturns.messages.itemAlreadyAdded"));
              return;
            }

            const newLine = recalcLineAmounts({
              article: p.id,
              articleId: p.id,
              articleLibelle: p.libelle,
              articleCode: p.code,
              quantiteRetournee: 1,
              quantiteOriginale: 1,
              prixUnitaireHT:
                p.prixVenteHT ||
                Number(p.prixVenteTTC1 || 0) / (1 + Number(p.tauxTVA || 0.2)),
              prixUnitaireTTC: p.prixVenteTTC1,
              tauxTVA: p.tauxTVA || 0.2,
              unite: p.unite,
            });

            setFieldValue("lignes", [...values.lignes, newLine]);
          };

          const mapDeliveryLineToReturnLine = (line) =>
            recalcLineAmounts({
              article: line.articleId,
              articleId: line.articleId,
              articleLibelle: line.articleNom || line.articleLibelle || "-",
              articleCode: line.articleCode || "",
              quantiteRetournee: Number(line.quantiteLivree ?? line.quantite ?? line.quantiteRetournee ?? 1) || 1,
              quantiteOriginale: Number(line.quantiteLivree ?? line.quantite ?? line.quantiteOriginale ?? 1) || 1,
              prixUnitaireHT: Number(line.prixUnitaireHT ?? 0),
              prixUnitaireTTC: Number(line.prixUnitaireTTC ?? 0),
              tauxTVA: Number(line.tauxTVA ?? 0.2),
              unite: line.unite || "",
            });

          const handleTicketNumberChange = (nextValue) => {
            const normalizedNext = (nextValue || "").trim();
            const normalizedLoaded = (loadedTicketNumber || "").trim();
            const hasChangedLoadedBL =
              normalizedLoaded &&
              normalizedNext.toUpperCase() !== normalizedLoaded.toUpperCase();

            setTicketNumber(nextValue);
            setTicketSearchPerformed(false);

            if (hasChangedLoadedBL) {
              setFieldValue("lignes", []);
              setFieldValue("numBL", "");
              setLoadedTicketNumber("");
            }
          };

          const getDeliveryStatus = (deliveryRecord) =>
            deliveryRecord?.statut || deliveryRecord?.status || "";

          const isReturnableDelivery = (deliveryRecord) =>
            getDeliveryStatus(deliveryRecord) === "LIVRE";

          const applyDeliveryToReturn = async (deliveryRecord) => {
            const request = ++ticketRequestRef.current;
            const bl = deliveryRecord?.body || deliveryRecord?.data?.body || deliveryRecord?.data || deliveryRecord;
            const deliveryStatus = getDeliveryStatus(bl);

            if (!isReturnableDelivery(bl)) {
              Swal.fire({
                icon: "warning",
                title: t("common.information"),
                text: `Le BL ${bl?.numero || ""} est au statut ${deliveryStatus || "-"}. Seuls les BL LIVRE peuvent être utilisés pour un retour.`,
              });
              return;
            }

            const lignes = Array.isArray(bl?.lignes) ? bl.lignes : [];

            if (!lignes.length) {
              Swal.fire({
                icon: "info",
                title: t("common.information"),
                text: "Aucune ligne trouvée pour ce ticket.",
              });
              return;
            }

            let remaining;
            try {
              const response = await customerReturnService.linked([bl.numero]);
              const fullTicket = await deliveryService.findByReference(bl.numero);
              remaining = remainingTicketLines(fullTicket, response.data[bl.numero] || []);
            } catch (error) {
              toast.error("Impossible de verifier les retours de ce ticket. Reessayez.");
              return;
            }
            if (request !== ticketRequestRef.current) return;
            if (!remaining.length) {
              toast.info("Tous les articles de ce ticket ont deja ete retournes.");
              return;
            }
            const mappedLines = remaining.map(mapDeliveryLineToReturnLine);
            setFieldValue("lignes", mappedLines);
            setFieldValue("numBL", bl?.numero || ticketNumber);

            if (bl?.clientId) {
              setFieldValue("clientId", bl.clientId);
            }

            setLoadedTicketNumber(bl?.numero || ticketNumber);
            setTicketNumber(bl?.numero || ticketNumber);
            setTicketSearchPerformed(false);
            toast.success("Ticket chargé avec succès.");
          };

          const handleSearchTickets = () => {
            const reference = (ticketNumber || "").trim();
            if (!reference) {
              Swal.fire({
                icon: "warning",
                title: t("sale.error"),
                text: "Veuillez saisir un numéro de ticket/BL.",
              });
              return;
            }

            setTicketSearchPerformed(true);
            dispatch(
              deliveryActions.listComptoire({
                query: reference,
                onError: (error) => {
                  Swal.fire({
                    icon: "error",
                    title: t("common.error"),
                    text: "Impossible de rechercher les tickets.",
                  });
                },
              })
            );
          };

          return (
            <Form className="pos-form-root">


              <Row className="g-2 mt-0 pos-workspace">
                <Col xl={7} lg={7} className="pos-col-stretch">
                  <Card className="pos-card pos-card-fill">
                    <CardBody className="pos-card-body">
                      <div className="d-flex gap-2 mb-3">
                        <Button
                          type="button"
                          color={returnSourceMode === "MANUAL" ? "primary" : "light"}
                          className="fw-bold"
                          onClick={() => {
                            if (returnSourceMode === "MANUAL") return;
                            ticketRequestRef.current++;
                            setReturnSourceMode("MANUAL");
                            setFieldValue("lignes", []);
                            setFieldValue("numBL", "");
                            setLoadedTicketNumber("");
                            setTicketNumber("");
                          }}
                          disabled={!canBeUpdated}
                        >
                          Sans ticket
                        </Button>
                        <Button
                          type="button"
                          color={returnSourceMode === "TICKET" ? "primary" : "light"}
                          className="fw-bold"
                          onClick={() => {
                            if (returnSourceMode === "TICKET") return;
                            ticketRequestRef.current++;
                            setReturnSourceMode("TICKET");
                            setFieldValue("lignes", []);
                            setFieldValue("numBL", "");
                            setLoadedTicketNumber("");
                            setTicketNumber("");
                          }}
                          disabled={!canBeUpdated}
                        >
                          Avec ticket
                        </Button>
                      </div>

                      {returnSourceMode === "TICKET" ? (
                        <div className="mb-3">
                          <Label className="pos-label mb-2">
                            <FaReceipt className="me-2" />
                            Numéro ticket / BL
                          </Label>
                          <div className="d-flex gap-2">
	                            <Input
	                              type="text"
	                              value={ticketNumber}
	                              readOnly
	                              inputMode="none"
	                              onClick={() => {
	                                if (!canBeUpdated || deliveryLoading) return;
	                                setTicketKeypadOpen(true);
	                              }}
	                              onKeyDown={(e) => {
	                                if (e.key === "Enter") {
	                                  e.preventDefault();
	                                  handleSearchTickets();
	                                }
	                              }}
                              placeholder="Ex: BL-000123"
                              className="pos-input"
                              disabled={!canBeUpdated || deliveryLoading}
                            />
                            <Button
                              type="button"
                              color="light"
                              onClick={() => setTicketKeypadOpen(true)}
                              disabled={!canBeUpdated || deliveryLoading}
                            >
                              <FaKeyboard />
                            </Button>
	                            <Button
	                              type="button"
	                              color="primary"
	                              onClick={handleSearchTickets}
	                              disabled={!canBeUpdated || deliveryLoading}
	                            >
	                              {deliveryLoading ? <Spinner size="sm" /> : <FaSearch />}
	                            </Button>
	                          </div>
	                          {loadedTicketNumber ? (
	                            <small className="text-success d-block mt-2">
	                              Ticket chargé: {loadedTicketNumber}
	                            </small>
	                          ) : null}
                            <div className="mt-3">
                              {deliveryLoading ? (
                                <div className="text-center py-3">
                                  <Spinner size="sm" color="primary" />
                                </div>
                              ) : ticketSearchPerformed ? (
                                deliverySearchResults.length === 0 ? (
                                  <div className="text-muted small">
                                    Aucun ticket trouvé pour cette recherche.
                                  </div>
                                ) : (
                                  <div className="d-grid gap-2">
                                    {deliverySearchResults.map((bl) => {
                                      const deliveryStatus = getDeliveryStatus(bl);
                                      const canUseForReturn = isReturnableDelivery(bl);

                                      return (
                                      <Card
                                        key={bl.id}
                                        className={`border shadow-sm ${canUseForReturn ? "" : "opacity-75"}`}
                                        style={{ cursor: canUseForReturn ? "pointer" : "not-allowed" }}
                                        onClick={() => {
                                          if (!canUseForReturn) return;
                                          applyDeliveryToReturn(bl);
                                        }}
                                      >
                                        <CardBody className="py-3">
                                          <div className="d-flex justify-content-between align-items-start gap-3">
                                            <div>
                                              <div className="fw-bold">{bl.numero || "-"}</div>
                                              <div className="text-muted small">
                                                {bl.clientNom || "Client non renseigné"}
                                              </div>
                                              <div className="small mt-1">
                                                <Badge color={canUseForReturn ? "success" : "secondary"}>
                                                  {deliveryStatus || "-"}
                                                </Badge>
                                                {!canUseForReturn ? (
                                                  <span className="text-danger ms-2">Non utilisable pour retour</span>
                                                ) : null}
                                              </div>
                                            </div>
                                            <div className="text-end">
                                              <div className="fw-bold text-primary">
                                                {formatPrice(bl.montantTotalTTC || 0)}
                                              </div>
                                              <Button
                                                type="button"
                                                size="sm"
                                                color={canUseForReturn ? "primary" : "secondary"}
                                                className="mt-2"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (!canUseForReturn) return;
                                                  applyDeliveryToReturn(bl);
                                                }}
                                                disabled={!canUseForReturn}
                                              >
                                                {canUseForReturn ? "Choisir" : "Indisponible"}
                                              </Button>
                                            </div>
                                          </div>
                                        </CardBody>
                                      </Card>
                                      );
                                    })}
                                  </div>
                                )
                              ) : null}
                            </div>
	                        </div>
	                      ) : null}

                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <Label className="pos-label mb-0">{t("product.searchPlaceholder")}</Label>
                          <Badge color="primary" pill className="pos-pill">
                            {totalElements || 0} articles
                          </Badge>
                        </div>
                        <div className="pos-view-toggle">
                          <Button
                            type="button"
                            color={displayMode === "card" ? "primary" : "light"}
                            className="rounded-3 border d-flex align-items-center gap-2"
                            onClick={() => setDisplayMode("card")}
                          >
                            <FaThLarge />
                            <span>Cards</span>
                          </Button>
                          <Button
                            type="button"
                            color={displayMode === "list" ? "primary" : "light"}
                            className="rounded-3 border d-flex align-items-center gap-2"
                            onClick={() => setDisplayMode("list")}
                          >
                            <FaList />
                            <span>List</span>
                          </Button>
                        </div>
                      </div>

	                      <div className="pos-modal-search">
	                        <Input
	                          innerRef={searchInputRef}
	                          type="text"
	                          value={searchValue}
	                          className="pos-input pos-nokeyboard"
	                          placeholder={t("product.searchPlaceholder")}
	                          readOnly
	                          inputMode="none"
	                          onClick={() => {
	                            if (!canAddProducts) return;
	                            setSearchKeypadOpen(true);
	                          }}
	                          onKeyDown={(e) => {
	                            if (e.key === "Enter") {
	                              e.preventDefault();
	                              triggerProductSearch(searchValue);
                            }
                          }}
                          disabled={!canAddProducts}
                        />
                        {modalSearchTerm ? (
                          <Button
                            type="button"
                            color="light"
                            className="pos-side-btn"
                            onClick={() => triggerProductSearch("")}
                          >
                            <FaTimes />
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          color="light"
                          className="pos-side-btn"
                          disabled={!canAddProducts}
                          onClick={() => setSearchKeypadOpen(true)}
                        >
                          <FaKeyboard />
                        </Button>
                        <Button
                          type="button"
                          color="primary"
                          className="pos-side-btn"
                          disabled={!canAddProducts}
                          onClick={() => triggerProductSearch(searchValue)}
                        >
                          <FaSearch />
                        </Button>
                      </div>

                      <div className={`pos-products-panel mt-3 ${displayMode === "card" ? "pos-products-card-panel" : "pos-products-list-panel"}`}>
                        {productsLoading ? (
                          <div className="text-center py-4">
                            <Spinner color="primary" />
                          </div>
                        ) : productList.length === 0 ? (
                          <div className="text-muted text-center py-4">
                            {t("product.noProducts")}
                          </div>
                        ) : displayMode === "list" ? (
                          productList.map((p) => {
                            const already = (values.lignes || []).some(
                              (l) => String(l.articleId) === String(p.id) || String(l.article) === String(p.id)
                            );
                            const unitLabel = unites?.find((u) => u.code === p.unite)?.libelle || p.unite || "-";
                            const stock = p.stockDisponible ?? p.stockPhysique ?? 0;

                            return (
                              <div
                                key={p.id}
                                className={`pos-product-row ${already ? "pos-product-row-added" : ""}`}
                                onClick={() => addProductToCurrentReturn(p)}
                              >
                                <div className="pos-product-main">
                                  <div className="fw-bold">{p.libelle || "-"}</div>
                                  <div className="text-muted small">
                                    Code: {p.codeBarre || p.code || "-"} | Stock: {stock} | Unit: {unitLabel}
                                  </div>
                                </div>
                                <div className="pos-product-price">{formatPrice(p.prixVenteTTC1 || 0)}</div>
                              </div>
                            );
                          })
                        ) : (
                          <Row xs={2} md={3} xl={4} className="g-3">
                            {productList.map((p) => {
                            const already = (values.lignes || []).some(
                              (l) => String(l.articleId) === String(p.id) || String(l.article) === String(p.id)
                            );
                            const unitLabel = unites?.find((u) => u.code === p.unite)?.libelle || p.unite || "-";
                            const stock = p.stockDisponible ?? p.stockPhysique ?? 0;
                            const productImage = p.photo
                              ? p.photo.startsWith("http")
                                ? p.photo
                                : `${config.baseURL}${p.photo}`
                              : null;

                            return (
                              <Col key={p.id}>
                                <div
                                  className={`pos-product-card ${already ? "pos-product-card-added" : ""}`}
                                  onClick={() => addProductToCurrentReturn(p)}
                                >
                                  <div className="pos-product-image">
                                    {productImage ? (
                                      <img
                                        src={productImage}
                                        alt={p.libelle || "Article"}
                                        className="product-img"
                                      />
                                    ) : (
                                      <FaImage size={28} className="text-muted" />
                                    )}
                                  </div>

                                  <div className="pos-product-body">
                                    <div className="pos-product-title">{p.libelle || "-"}</div>
                                    <div className="pos-product-code">{p.codeBarre || p.code || "-"}</div>

                                    <div className="pos-product-meta">
                                      <span className="unit">{unitLabel}</span>
                                      <span className={`stock ${stock <= 0 ? "out" : stock < 5 ? "low" : "ok"}`}>
                                        {stock}
                                      </span>
                                    </div>

                                    <div className="pos-product-footer">
                                      <span className="price">{formatPrice(p.prixVenteTTC1 || 0)}</span>
                                      <button
                                        type="button"
                                        className="add-btn"
                                        disabled={!canAddProducts || already}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          addProductToCurrentReturn(p);
                                        }}
                                        title={t("cart.add")}
                                      >
                                        <FaCartPlus />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            );
                            })}
                          </Row>
                        )}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          {t("product.showing", {
                            start: (currentPage - 1) * (pageSize || 10) + 1,
                            end: Math.min(currentPage * (pageSize || 10), totalElements || 0),
                            total: totalElements || 0,
                            currentPage,
                            totalPages: totalPages || 1,
                          })}
                        </small>

                        <div className="d-flex gap-2">
                          <Button
                            type="button"
                            color="secondary"
                            disabled={currentPage <= 1 || !canAddProducts}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          >
                            {"<"}
                          </Button>
                          <Button
                            type="button"
                            color="secondary"
                            disabled={totalPages ? currentPage >= totalPages || !canAddProducts : true}
                            onClick={() => setCurrentPage((p) => p + 1)}
                          >
                            {">"}
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={5} lg={5} className="pos-col-stretch pos-right pos-cart-pane">
                  <Card className="pos-card shadow-sm rounded-4 border-0 mb-3">
                    <CardBody className="pos-card-body">
                      <FormGroup className="mb-2">
                        <Label className="fw-bold text-secondary d-flex justify-content-between align-items-center">
                          <span>
                            <FaUser className="me-1" /> {t("commande.client")}
                          </span>
                        </Label>

                        <Button
                          color="light"
                          className="w-100 text-start fw-bold py-2 rounded-3 border"
                          onClick={() => {
                            setClientSearch("");
                            toggleClientModal();
                          }}
                          disabled={!canBeUpdated}
                        >
                          {values.clientId ? getClientLabelById(values.clientId) : t("common.selectOption")}
                        </Button>
                      </FormGroup>
                    </CardBody>
                  </Card>

                  <Card className="pos-card pos-card-fill pos-cart-card shadow-sm rounded-4 border-0">
                    <CardHeader className="bg-light fw-bold d-flex justify-content-between align-items-center flex-shrink-0">
                        <span>
                          <FaShoppingCart className="me-2" />
                          {t("customerReturns.form.items", "Articles retour")}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          {lineCount > 0 && <Badge color="primary">{lineCount}</Badge>}
                          {lineCount > 0 && canBeUpdated && (
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              outline
                              onClick={() => setFieldValue("lignes", [])}
                              title={t("cart.clear", "Vider")}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </div>
                    </CardHeader>

                    <CardBody className="p-0 d-flex flex-column h-100">
                      <FieldArray name="lignes">
                        {({ remove }) => (
                          <div className="pos-return-lines pos-cart-items">
                            {values.lignes.length === 0 ? (
                              <div className="text-center text-muted py-4">
                                {t("customerReturns.form.noItems")}
                              </div>
                            ) : (
                              values.lignes.map((line, index) => (
                                <div key={index} className="pos-return-line pos-cart-line">
                                  <div className="pos-sale-line-layout d-flex justify-content-between align-items-center">
                                    <div className="flex-grow-1 me-2">
                                      <strong className="d-block">{line.articleLibelle || "Article"}</strong>

                                      <div className="d-flex gap-2 mt-1 align-items-center">
                                        <Input
                                          value={line.quantiteRetournee ?? 1}
                                          readOnly
                                          disabled={!canBeUpdated}
                                          className="pos-cart-input"
                                          onClick={() => {
                                            if (!canBeUpdated) return;
                                            setNumberField({ type: "QTE", index });
                                            setNumberKeypadValue(String(line.quantiteRetournee ?? 1));
                                            setNumberKeypadOpen(true);
                                          }}
                                        />
                                        <Button
                                          type="button"
                                          color="secondary"
                                          outline
                                          size="sm"
                                          disabled={!canBeUpdated}
                                          className="d-inline-flex align-items-center justify-content-center"
                                          title="Clavier numérique"
                                          onClick={() => {
                                            setNumberField({ type: "QTE", index });
                                            setNumberKeypadValue(String(line.quantiteRetournee ?? 1));
                                            setNumberKeypadOpen(true);
                                          }}
                                        >
                                          <FaKeyboard />
                                        </Button>
                                      </div>

                                      <div className="small text-muted mt-1">
                                        PU: <span className="fw-semibold">{formatPrice(line.prixUnitaireTTC || 0, false)} DH</span>
                                        {" / "}
                                        {unites?.find((u) => u.code === line.unite)?.libelle || line.unite || "-"}
                                      </div>
                                    </div>

                                    <div className="text-end d-flex flex-column align-items-end">
                                      <strong>{formatPrice(line.montantTTC || 0, false)}</strong>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        outline
                                        disabled={!canBeUpdated}
                                        className="mt-1"
                                        onClick={() => remove(index)}
                                      >
                                        <FaTrashAlt />
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                      <div className="fw-bold">{line.articleLibelle || "Article"}</div>
                                      <div className="text-muted small">{line.articleCode || "-"}</div>
                                    </div>
                                    <div className="text-end d-flex flex-column align-items-end">
                                      <strong>{formatPrice(line.montantTTC || 0, false)}</strong>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        outline
                                        className="mt-1"
                                        onClick={() => remove(index)}
                                        disabled={!canBeUpdated}
                                      >
                                        <FaTrashAlt />
                                      </Button>
                                    </div>
                                  </div>

                                  <div className="d-flex gap-2 mt-1 align-items-center">
                                    <Input
                                      value={line.quantiteRetournee ?? 1}
                                      readOnly
                                      className="pos-cart-input text-center"
                                      onClick={() => {
                                        if (!canBeUpdated) return;
                                        setNumberField({ type: "QTE", index });
                                        setNumberKeypadValue(String(line.quantiteRetournee ?? 1));
                                        setNumberKeypadOpen(true);
                                      }}
                                    />
                                    <Button
                                      type="button"
                                      color="secondary"
                                      outline
                                      size="sm"
                                      disabled={!canBeUpdated}
                                      className="d-inline-flex align-items-center justify-content-center"
                                      title="Clavier numérique"
                                      onClick={() => {
                                        setNumberField({ type: "QTE", index });
                                        setNumberKeypadValue(String(line.quantiteRetournee ?? 1));
                                        setNumberKeypadOpen(true);
                                      }}
                                    >
                                      <FaKeyboard />
                                    </Button>
                                    <div className="small text-muted">
                                      PU: <span className="fw-semibold">{formatPrice(line.prixUnitaireTTC || 0, false)} DH</span>
                                      {" / "}
                                      {unites?.find((u) => u.code === line.unite)?.libelle || line.unite || "-"}
                                    </div>
                                  </div>

                                  <Row className="g-2">
                                    <Col xs={4}>
                                      <Label className="pos-label">Qté</Label>
                                      <Input
                                        value={line.quantiteRetournee ?? 1}
                                        readOnly
                                        className="form-control pos-mini-input text-center"
                                        onClick={() => {
                                          setNumberField({ type: "QTE", index });
                                          setNumberKeypadValue(String(line.quantiteRetournee ?? 1));
                                          setNumberKeypadOpen(true);
                                        }}
                                      />
                                    </Col>
                                    <Col xs={4}>
                                      <Label className="pos-label">PU TTC</Label>
                                      <Input
                                        value={line.prixUnitaireTTC ?? 0}
                                        readOnly
                                        className="form-control pos-mini-input text-end"
                                        onClick={() => {
                                          if (!canBeUpdated || values.numBL) return;
                                          setNumberField({ type: "PU", index });
                                          setNumberKeypadValue(String(line.prixUnitaireTTC ?? 0));
                                          setNumberKeypadOpen(true);
                                        }}
                                      />
                                    </Col>
                                    <Col xs={4}>
                                      <Label className="pos-label">Unité</Label>
                                      <Input
                                        value={unites?.find((u) => u.code === line.unite)?.libelle || line.unite || "-"}
                                        readOnly
                                        className="form-control pos-mini-input text-center"
                                      />
                                    </Col>
                                  </Row>

                                  <div className="d-flex justify-content-end mt-2 fw-bold">
                                    {formatPrice(line.montantTTC || 0)}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </FieldArray>

                      {errors.lignes && typeof errors.lignes === "string" ? (
                        <div className="text-danger mt-2 fw-bold">{errors.lignes}</div>
                      ) : null}

                      <div className="pos-cart-footer">
                        <div className="d-flex justify-content-between fw-bold fs-5">
                          <span>{t("cart.totalTTC", "Total TTC")}</span>
                          <strong>{formatPrice(totals.montantTTC)}</strong>
                        </div>

                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <Button
                          color="success"
                          size="lg"
                          className="flex-fill fw-bold"
                          type="button"
                          disabled={isSubmitting || !canSave}
                          onClick={openReturnCheckout}
                        >
                          {t("common.saveAndClose")}
                        </Button>
                        <Button
                          color="primary"
                          size="lg"
                          className="flex-fill fw-bold"
                          type="submit"
                          disabled={isSubmitting || !canSave}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              {t("common.loading")}
                            </>
                          ) : (
                            t("common.save")
                          )}
                        </Button>
                        {!isAdmin && (
                          <Button
                            color="info"
                            outline
                            size="lg"
                            className="fw-bold"
                            onClick={() => navigate(RETOUR_PAGE)}
                          >
                            {t("common.list", "Liste")}
                          </Button>
                        )}
                      </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              {/* ========================= CLIENT MODAL ========================= */}
              <Modal isOpen={clientModal} toggle={toggleClientModal} size="lg">
                <ModalHeader toggle={toggleClientModal}>
                  <div className="d-flex align-items-center gap-2">
                    <FaUser />
                    <span>Choisir un client</span>
                  </div>
                </ModalHeader>

                <ModalBody>
                  <div className="pos-modal-search">
                    <Input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="pos-input"
                      placeholder="Rechercher (Nom / Entreprise / Code)"
                      autoFocus
                    />
                    {clientSearch ? (
                      <Button
                        type="button"
                        color="light"
                        className="pos-side-btn"
                        onClick={() => setClientSearch("")}
                      >
                        <FaTimes />
                      </Button>
                    ) : null}
                  </div>

                  <div className="pos-modal-table-wrap mt-3">
                    <Table hover bordered className="align-middle mb-0 pos-table">
                      <thead className="pos-thead">
                        <tr>
                          <th style={{ width: 120 }}>Code</th>
                          <th>Nom / Entreprise</th>
                          <th style={{ width: 120 }} className="text-center">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientList.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="text-center py-4">
                              <div className="text-muted">Aucun client</div>
                            </td>
                          </tr>
                        ) : (
                          clientList.map((c) => (
                            <tr key={c.id} style={{ cursor: "pointer" }}>
                              <td className="fw-bold">{c.code || "-"}</td>
                              <td>
                                <div className="fw-bold">{c.label}</div>
                              </td>
                              <td className="text-center">
                                <Button
                                  type="button"
                                  size="sm"
                                  color="primary"
                                  onClick={() => {
                                    setFieldValue("clientId", c.id);
                                    toggleClientModal();
                                  }}
                                >
                                  Choisir
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </ModalBody>

                <ModalFooter>
                  <Button color="secondary" type="button" onClick={toggleClientModal}>
                    Fermer
                  </Button>
	                </ModalFooter>
	              </Modal>

              <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} size="lg" centered>
                <ModalHeader toggle={() => setPaymentModal(false)}>
                  Confirmer le remboursement
                </ModalHeader>
                <ModalBody>
                  <Card className="border-0 shadow-sm bg-light mb-3">
                    <CardBody>
                      <Row className="g-3 align-items-center">
                        <Col md={6}>
                          <div className="text-muted fw-bold text-uppercase small mb-2">
                            <FaMoneyBillWave className="me-2 text-success" />
                            Montant à rembourser
                          </div>
                          <div className="fs-3 fw-bold text-success">
                            {formatPrice(totals.montantTTC)}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="text-muted fw-bold text-uppercase small mb-2">
                            <FaCreditCard className="me-2 text-primary" />
                            Mode de paiement
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              type="button"
                              color={paidCash > 0 ? "primary" : "secondary"}
                              className="d-flex align-items-center px-4 py-2 fw-bold"
                              onClick={() => selectReturnPayment("ESPECES")}
                            >
                              <FaMoneyBillWave className="me-2" />
                              Espèces
                            </Button>
                            <Button
                              type="button"
                              color={paidCard > 0 ? "primary" : "secondary"}
                              className="d-flex align-items-center px-4 py-2 fw-bold"
                              onClick={() => selectReturnPayment("CARTE_BANCAIRE")}
                            >
                              <FaCreditCard className="me-2" />
                              Carte
                            </Button>
                          </div>
                        </Col>
                      </Row>

                      <Row className="g-3 mt-2">
                        <Col md={4}>
                          <FormGroup className="mb-0">
                            <Label className="fw-bold text-muted">
                              <FaMoneyBillWave className="me-2 text-success" />
                              Espèces
                            </Label>
                            <Input
                              type="number"
                              readOnly
                              value={paidCash}
                              className="bg-white py-2 fs-5 fw-bold"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup className="mb-0">
                            <Label className="fw-bold text-muted">
                              <FaCreditCard className="me-2 text-primary" />
                              Carte
                            </Label>
                            <Input
                              type="number"
                              readOnly
                              value={paidCard}
                              className="bg-white py-2 fs-5 fw-bold"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup className="mb-0">
                            <Label className="fw-bold text-muted">Total payé</Label>
                            <Input
                              type="number"
                              readOnly
                              value={paidTotal}
                              className="bg-white py-2 fs-5 fw-bold text-success"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </ModalBody>
                <ModalFooter className="d-flex justify-content-end">
                  <Button
                    color="primary"
                    size="lg"
                    className="px-4"
                    disabled={isSubmitting || paidTotal <= 0}
                    onClick={confirmReturnCheckout}
                  >
                    {isSubmitting && <Spinner size="sm" className="me-2" />}
                    {t("payment.confirm", "Confirmer")}
                  </Button>
                  <Button
                    color="secondary"
                    size="lg"
                    className="px-4 ms-2"
                    disabled={isSubmitting}
                    onClick={() => setPaymentModal(false)}
                  >
                    {t("payment.cancel", "Annuler")}
                  </Button>
                </ModalFooter>
              </Modal>
	
	              <POSKeypadModal
                isOpen={textKeypadOpen}
                toggle={() => setTextKeypadOpen(false)}
                value={textKeypadValue}
                onChange={setTextKeypadValue}
                onSearch={() => {
                  setFieldValue(textFieldName, textKeypadValue);
                  setTextKeypadOpen(false);
                }}
                title="Saisir Motif"
              />

              <POSKeypadModal
                isOpen={searchKeypadOpen}
                toggle={() => setSearchKeypadOpen(false)}
                value={searchValue}
                onChange={setSearchValue}
                onSearch={() => {
                  triggerProductSearch(searchValue);
                  setSearchKeypadOpen(false);
                }}
                title="Recherche Produit"
              />

              <POSKeypadModal
                isOpen={ticketKeypadOpen}
                toggle={() => setTicketKeypadOpen(false)}
                value={ticketNumber}
                onChange={handleTicketNumberChange}
                onSearch={() => {
                  handleSearchTickets();
                  setTicketKeypadOpen(false);
                }}
                title="Numéro Ticket / BL"
              />

              <POSNumericKeypadModal
                isOpen={numberKeypadOpen}
                toggle={() => setNumberKeypadOpen(false)}
                value={numberKeypadValue}
                onChange={setNumberKeypadValue}
                onConfirm={(val) => {
                  const num = Number(val) || 0;

                  if (numberField?.type === "QTE") {
                    if (num <= 0 || (values.numBL && num > Number(values.lignes[numberField.index].quantiteOriginale))) {
                      toast.error("Quantite invalide pour ce ticket.");
                      return;
                    }
                    const updatedLine = recalcLineAmounts({
                      ...values.lignes[numberField.index],
                      quantiteRetournee: num,
                    });
                    setFieldValue(`lignes.${numberField.index}`, updatedLine);
                  }

                  if (numberField?.type === "PU" && !values.numBL) {
                    const updatedLine = recalcLineAmounts({
                      ...values.lignes[numberField.index],
                      prixUnitaireTTC: num,
                    });
                    setFieldValue(`lignes.${numberField.index}`, updatedLine);
                  }

                  setNumberKeypadOpen(false);
                }}
                title="Saisie numérique"
              />
            </Form>
          );
        }}

      </Formik>
    </Container>
  );
};

export default CustomerReturnForm;



